'use client'

import dynamic from 'next/dynamic'
import { useState } from 'react'
import { MapPin } from 'lucide-react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { updateUser } from '@/app/actions/user'
import { validateAddress } from '@/app/actions/validate-address'
import { State } from '@/blocks/Form/State'
import { Text } from '@/blocks/Form/Text'
import { Width } from '@/blocks/Form/Width'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { inputFocusClasses } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formValidation } from '@/utilities/formValidation'
import { normalizeLocationFields } from '@/utilities/location/locationFields'
import { parseUSState } from '@/utilities/location/parseUSState'
import { cn } from '@/utilities/ui'

import { ProfileSteps } from './ProfileStep'

import type { User } from '@/payload-types'
import type { LocationFields } from '@/utilities/location/locationFields'
import type { FieldValues, UseFormRegister } from 'react-hook-form'

function AddressAutocompleteFallback() {
  return (
    <Width>
      <Label htmlFor="address">Address</Label>
      <input
        id="address"
        type="text"
        disabled
        placeholder="Enter Address"
        className={cn(
          'flex w-full rounded-xl border border-border bg-input p-3 font-family-body text-base text-primary placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-reg',
          inputFocusClasses,
        )}
      />
    </Width>
  )
}

const AddressAutocomplete = dynamic(
  () => import('./AddressAutocomplete').then((module) => module.AddressAutocomplete),
  { ssr: false, loading: () => <AddressAutocompleteFallback /> },
)

type SuggestedAddress = {
  location: LocationFields
  formattedAddress: string
}

function locationFieldsMatch(a: LocationFields, b: LocationFields): boolean {
  return (
    (a.address ?? '').trim() === (b.address ?? '').trim() &&
    (a.address2 ?? '').trim() === (b.address2 ?? '').trim() &&
    (a.city ?? '').trim() === (b.city ?? '').trim() &&
    (a.state ?? '').trim() === (b.state ?? '').trim() &&
    (a.zip ?? '').trim() === (b.zip ?? '').trim()
  )
}

/** Shared location form for Guided Introduction and Settings. */
export const LocationForm = ({
  user,
  setOpenDrawer,
  setCurrentProfileStep,
  onUserUpdated,
}: {
  user: User
  setOpenDrawer: (open: boolean) => void
  setCurrentProfileStep: (step: ProfileSteps) => void
  onUserUpdated?: (updatedUser?: User) => Promise<void>
}) => {
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [suggestion, setSuggestion] = useState<SuggestedAddress | null>(null)
  const methods = useForm<LocationFields>({
    defaultValues: {
      address: user.location?.location?.address ?? '',
      address2: user.location?.location?.address2 ?? '',
      city: user.location?.location?.city ?? '',
      state: user.location?.location?.state ?? '',
      zip: user.location?.location?.zip ?? '',
    },
  })

  const saveLocation = async (data: LocationFields): Promise<boolean> => {
    const normalized = normalizeLocationFields(data)
    const newLocation: User['location'] = {
      location: {
        address: normalized.address ?? '',
        address2: normalized.address2 ?? '',
        city: normalized.city ?? '',
        zip: normalized.zip ?? '',
      },
    }
    const state = parseUSState(normalized.state)
    if (state) {
      newLocation.location!.state = state
    }

    const res = await updateUser({
      ...user,
      location: newLocation,
    })

    if (res.status === 'error') {
      setSubmitError(res.message)
      return false
    }

    const updatedUser = res.data

    if (updatedUser) {
      methods.reset({
        address: updatedUser.location?.location?.address ?? '',
        address2: updatedUser.location?.location?.address2 ?? '',
        city: updatedUser.location?.location?.city ?? '',
        state: updatedUser.location?.location?.state ?? '',
        zip: updatedUser.location?.location?.zip ?? '',
      })
    }
    setOpenDrawer(false)
    setCurrentProfileStep('location')
    await onUserUpdated?.(updatedUser)
    toast.success('Profile updated successfully')
    return true
  }

  const applyLocationToForm = (data: LocationFields) => {
    const normalized = normalizeLocationFields(data)
    methods.setValue('address', normalized.address ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    methods.setValue('address2', normalized.address2 ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    })
    methods.setValue('city', normalized.city ?? '', { shouldDirty: true, shouldValidate: true })
    methods.setValue('state', normalized.state ?? '', { shouldDirty: true, shouldValidate: true })
    methods.setValue('zip', normalized.zip ?? '', { shouldDirty: true, shouldValidate: true })
  }

  const onSubmit = async (data: LocationFields) => {
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      const result = await validateAddress(normalizeLocationFields(data))

      if (result.status === 'empty') {
        await saveLocation(data)
        return
      }

      if (result.status === 'invalid') {
        setSubmitError(result.message)
        return
      }

      if (result.status === 'confirm') {
        const alreadyApplied = locationFieldsMatch(data, result.location)
        if (alreadyApplied) {
          applyLocationToForm(result.location)
          await saveLocation(result.location)
          return
        }

        setSuggestion({
          location: result.location,
          formattedAddress: result.formattedAddress,
        })
        return
      }

      applyLocationToForm(result.location)
      await saveLocation(result.location)
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error ? error.message : 'An error occurred while submitting the form',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUseSuggestedAddress = async () => {
    if (!suggestion) return
    const location = suggestion.location
    applyLocationToForm(location)
    setSuggestion(null)
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await saveLocation(location)
    } catch (error: unknown) {
      setSubmitError(
        error instanceof Error ? error.message : 'An error occurred while submitting the form',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form id="social-form" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="m-auto my-6 max-w-xl">
          <h2 className="font-family-body text-2xl font-semibold text-foreground">Location</h2>
          <p className="mt-3 font-body text-card-foreground">
            Add your location details to tailor content, opportunities, or events available in your
            area.
          </p>
          <div className="my-4 grid grid-cols-1 gap-4 *:min-w-0 md:grid-cols-2">
            <div className="col-span-2">
              <AddressAutocomplete />
            </div>
            <div className="col-span-2 md:col-span-1">
              <State
                name="state"
                label="State"
                errors={methods.formState.errors}
                control={methods.control}
                blockType="state"
              />
            </div>
            <div className="col-span-2 md:col-span-1">
              <Text
                name="zip"
                label="Zip Code"
                blockType="text"
                placeholder="Enter Zip Code"
                errors={methods.formState.errors}
                register={methods.register as unknown as UseFormRegister<FieldValues>}
                validation={formValidation.zipCode}
              />
            </div>
            <Button
              type="submit"
              className="col-span-2 block text-center"
              labelClassName="w-fit mx-auto"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {submitError && (
            <p className="m-auto rounded-md bg-red-50 p-3 text-center text-sm text-red-500">
              {submitError}
            </p>
          )}
        </div>
      </form>
      <AlertDialog
        open={suggestion !== null}
        onOpenChange={(open) => {
          if (!open) setSuggestion(null)
        }}
      >
        <AlertDialogContent size="sm" overlayVariant="blur">
          <AlertDialogHeader>
            <AlertDialogMedia className="rounded-full bg-evergreen-50 text-evergreen-700">
              <MapPin />
            </AlertDialogMedia>
            <AlertDialogTitle>Did you mean this address?</AlertDialogTitle>
            <AlertDialogDescription>
              {suggestion?.formattedAddress ??
                'A close match was found. Use the suggested address to continue.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button variant="accent" color="inverse" icon={false}>
                Edit
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button variant="primary" icon={false} onClick={handleUseSuggestedAddress}>
                Use this address
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  )
}
